import React, { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash.debounce';
import * as mainService from '@app/services/mainService';
import DaohuuLayoutComponent from '@app/pages/FUtil/DaohuuLayoutComponent';

const DaohuuAutocompleteComponent = ({ path_access, currentSuggestion, onChange, id_tableName }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const abortControllerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const fetchSuggestions = useCallback(async (newQuery) => {
    if (newQuery.length === 0) {
      setSuggestions([]);
      setShowDropdown(false);
      setIsLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsLoading(true);
    setError(null);

    try {
      const response = await mainService.getAutocompletedaohuu({
        path_access: path_access,
        id_tableName: id_tableName,
        search_text: newQuery,
        signal: abortControllerRef.current.signal
      });

      if (!abortControllerRef.current.signal.aborted) {
        setSuggestions(Array.isArray(response) ? response : []);
        setShowDropdown(true);
        setSelectedIndex(-1);
        setIsLoading(false);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching suggestions:', error);
        setError('Không thể tải dữ liệu');
        setSuggestions([]);
        setShowDropdown(false);
      }
      setIsLoading(false);
    }
  }, [path_access, id_tableName]);

  const debouncedFetchSuggestions = useCallback(
      debounce(fetchSuggestions, 300),
      [fetchSuggestions]
  );

  useEffect(() => {
    return () => {
      debouncedFetchSuggestions.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedFetchSuggestions]);

  useEffect(() => {
    debouncedFetchSuggestions(query);
  }, [query, debouncedFetchSuggestions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    setError(null);
    if (value.length === 0) {
      setShowDropdown(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery('');
    setSuggestions([]);
    setShowDropdown(false);
    setIsFocused(false);
    onChange({ value: suggestion });
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    if (suggestions.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = () => {
    // Delay to allow dropdown click
    setTimeout(() => setIsFocused(false), 150);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setShowDropdown(false);
      setSelectedIndex(-1);
      inputRef.current?.blur();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex(prev => prev < suggestions.length - 1 ? prev + 1 : 0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : suggestions.length - 1);
    } else if (event.key === 'Enter' && selectedIndex >= 0) {
      event.preventDefault();
      handleSuggestionClick(suggestions[selectedIndex]);
    }
  };

  // Enhanced selected user display
  if (currentSuggestion) {
    return (
        <div
            className="selected-user-display"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 4px 8px 12px', // Giảm padding phải để nút close sát biên
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease',
              height: '38px'
            }}
        >
          {/* Avatar with enhanced styling */}
          <div style={{ position: 'relative', marginRight: '10px' }}>
            {currentSuggestion.image ? (
                <img
                    src={currentSuggestion.image}
                    alt={currentSuggestion.ten}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '2px solid #ffffff',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                />
            ) : (
                <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: '600',
                      boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                    }}
                >
                  {currentSuggestion.ten?.charAt(0)?.toUpperCase() || 'U'}
                </div>
            )}
            <div
                style={{
                  position: 'absolute',
                  bottom: '-2px',
                  right: '-2px',
                  width: '10px',
                  height: '10px',
                  background: '#10b981',
                  borderRadius: '50%',
                  border: '2px solid white'
                }}
            />
          </div>

          {/* User info with more space */}
          <div style={{ flex: 1, minWidth: 0, marginRight: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1f2937',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '150px'
                }}
            >
              {currentSuggestion.ten}
            </span>
              {currentSuggestion.ma && (
                  <span
                      style={{
                        fontSize: '9px',
                        fontWeight: '700',
                        color: '#1f2937',
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        padding: '1px 5px',
                        borderRadius: '4px',
                        border: '1px solid #f59e0b',
                        boxShadow: '0 1px 3px rgba(245, 158, 11, 0.2)',
                        lineHeight: '1',
                        display: 'inline-block'
                      }}
                  >
                {currentSuggestion.ma}
              </span>
              )}
            </div>
            {currentSuggestion.ngay_sinh && (
                <div
                    style={{
                      fontSize: '11px',
                      color: '#6b7280',
                      marginTop: '1px',
                      fontWeight: '400'
                    }}
                >
                  {currentSuggestion.ngay_sinh}
                </div>
            )}
          </div>

          {/* Compact close button - sát biên phải */}
          <button
              type="button"
              onClick={() => onChange({ value: null })}
              style={{
                width: '18px',
                height: '18px',
                borderRadius: '4px',
                border: 'none',
                background: 'rgba(239, 68, 68, 0.1)',
                color: '#ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#ef4444';
                e.target.style.color = 'white';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                e.target.style.color = '#ef4444';
                e.target.style.transform = 'scale(1)';
              }}
              title="Xóa lựa chọn"
          >
            <i className="fas fa-times" style={{ fontSize: '8px' }}></i>
          </button>
        </div>
    );
  }

  return (
      <div className="autocomplete-container" ref={dropdownRef} style={{ position: 'relative' }}>
        {/* Enhanced input field */}
        <div style={{ position: 'relative' }}>
          <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              placeholder="Tìm kiếm người dùng..."
              style={{
                width: '100%',
                height: '38px',
                padding: '8px 40px 8px 12px',
                border: `2px solid ${error ? '#ef4444' : isFocused ? '#3b82f6' : '#e2e8f0'}`,
                borderRadius: '8px',
                fontSize: '14px',
                transition: 'all 0.3s ease',
                background: isFocused ? '#ffffff' : '#f8fafc',
                outline: 'none',
                boxShadow: isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none'
              }}
              aria-label="Search for users"
              aria-expanded={showDropdown}
              aria-haspopup="listbox"
          />

          {/* Search icon */}
          <div
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: isLoading ? '#3b82f6' : '#9ca3af',
                pointerEvents: 'none'
              }}
          >
            {isLoading ? (
                <div
                    style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #e5e7eb',
                      borderTop: '2px solid #3b82f6',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}
                />
            ) : (
                <i className="fas fa-search" style={{ fontSize: '14px' }}></i>
            )}
          </div>
        </div>

        {/* Enhanced error display */}
        {error && (
            <div
                style={{
                  marginTop: '4px',
                  padding: '8px 12px',
                  background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                  border: '1px solid #fca5a5',
                  borderRadius: '6px',
                  color: '#dc2626',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
            >
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
        )}

        {/* Enhanced dropdown */}
        {showDropdown && suggestions.length > 0 && (
            <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
                  zIndex: 1050,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  marginTop: '4px',
                  backdropFilter: 'blur(10px)'
                }}
                role="listbox"
            >
              {suggestions.map((suggestion, index) => (
                  <div
                      key={suggestion.id || index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleSuggestionClick(suggestion);
                        }
                      }}
                      role="option"
                      tabIndex={0}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 8px 8px 6px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        background: index === selectedIndex
                            ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
                            : 'transparent',
                        borderBottom: index < suggestions.length - 1 ? '1px solid #f3f4f6' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (index !== selectedIndex) {
                          e.target.style.background = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (index !== selectedIndex) {
                          e.target.style.background = 'transparent';
                        }
                      }}
                  >
                    {/* Compact avatar - sát biên trái */}
                    <div style={{ marginRight: '8px', flexShrink: 0 }}>
                      {suggestion.image ? (
                          <img
                              src={suggestion.image}
                              alt={suggestion.ten}
                              style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: '1px solid #e5e7eb'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                          />
                      ) : (
                          <div
                              style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                background: '#6b7280',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: '600'
                              }}
                          >
                            {suggestion.ten?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                      )}
                    </div>

                    {/* Thông tin user - tối ưu không gian */}
                    <div style={{ flex: 1, minWidth: 0, lineHeight: '1.2' }}>
                      {/* Tên đầy đủ trên 1 dòng */}
                      <div
                          style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={suggestion.ten} // Tooltip để xem tên đầy đủ
                      >
                        {suggestion.ten}
                      </div>

                      {/* Dòng 2: Badge mã + năm sinh */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {suggestion.ma && (
                            <span
                                style={{
                                  fontSize: '9px',
                                  fontWeight: '700',
                                  color: '#1f2937',
                                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                  padding: '1px 4px',
                                  borderRadius: '3px',
                                  border: '1px solid #f59e0b',
                                  lineHeight: '1'
                                }}
                            >
                      {suggestion.ma}
                    </span>
                        )}
                        {suggestion.ngay_sinh && (
                            <span
                                style={{
                                  fontSize: '11px',
                                  color: '#6b7280',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                            >
                      <i className="fas fa-calendar-alt" style={{ fontSize: '9px' }}></i>
                              {suggestion.ngay_sinh}
                    </span>
                        )}
                      </div>
                    </div>

                    {/* Arrow nhỏ gọn */}
                    <div style={{ marginLeft: '4px', color: '#d1d5db', flexShrink: 0 }}>
                      <i className="fas fa-chevron-right" style={{ fontSize: '10px' }}></i>
                    </div>
                  </div>
              ))}
            </div>
        )}

        {/* Enhanced no results */}
        {showDropdown && suggestions.length === 0 && query.length > 0 && !isLoading && !error && (
            <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                  zIndex: 1050,
                  marginTop: '4px',
                  padding: '24px 16px',
                  textAlign: 'center'
                }}
            >
              <div style={{ color: '#9ca3af', fontSize: '48px', marginBottom: '12px' }}>
                <i className="fas fa-search"></i>
              </div>
              <div style={{ color: '#374151', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                Không tìm thấy kết quả
              </div>
              <div style={{ color: '#6b7280', fontSize: '12px' }}>
                Thử với từ khóa khác
              </div>
            </div>
        )}

        {/* Spinning animation CSS */}
        <style>
          {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
        </style>
      </div>
  );
};

export default DaohuuAutocompleteComponent;